import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { isEmpty, get, isEqual } from 'lodash';
import moment from 'moment';
import { getLabel } from 'utils/getLabel';

// local dependencies
import { popover } from 'modules/popover';
import { calendar as Calendar } from 'modules/calendar';
import { Icon } from 'modules/icon-bank';

/************************************************/

// calendar view
const CalendarView = ( props ) => {

    return (
        <div className='ui-calendar-view' >

            { !isEmpty( props.label ) && (
                <div className='ui-calendar-view__label' >
                    
                    {/* label text */}
                    <div className='ui-radio-list__control__label__text'>{ getLabel( props.label ) }</div>

                    {/* required asterisk */}
                    {
                        props.required ? (
                            <div className='ui-radio-list__control__label__asterisk'>*</div>
                        ) : null
                    }
                </div>
            ) }
            
            <Calendar
                className={ props.className }
                value={ props.value }
                minDate={ props.minDate }
                onChange={ ( selection ) => {
                    props.onChange( selection );

                    // close popover on selection
                    if( props.closeOnSelection ) {
                        setTimeout( popover.close, 300 );
                    }
                } }
            />
        </div>
       
    );
};

// set display name
CalendarView.displayName = 'dateField.CalendarView';


/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { dateField as DateField } from 'ui/views/generic/dateField';
 *
 * // render
 * <DateField propName='propValue'/>
 */
export const dateField = ( props ) => {

    const [ isOpen, openClose ] = useState( false );

    useEffect( () => {
        openClose( false );
        popover.close();
    }, [ props.closePopoverOnScrollTrigger ] );

    // get error message (ref)
    const errorMessage = get( props.error, 'message', null );
    const hasError = ! isEmpty( errorMessage );

    // widget class name
    const widgetClassName = classnames( 'ui-date-field', props.className );

    //Dates Extracting and checking if empty then select 1st date of the current month to current Date
    const value = ! isEmpty( props.value ) ? moment( props.value ).format( 'MM-DD-YYYY' ) : null;

    // set a min date (tomorrow) if back date selection is disabled
    const minDate = get( props, 'config.disableBackDateSelection', false ) ? moment().add( 1, 'days' ).format( 'MM-DD-YYYY' ) : null;

    const readonly = get( props, 'config.readonly', false );

    // get classname
    const inputClassName = classnames( 'ui-date-field__input', {
        'ui-date-field__input--disabled': readonly,
        'ui-date-field__input--error': hasError,
    } );

    // open popover datepicker
    const openPopover = useCallback( ( element ) => {
        popover.open( {
            referenceElement: element,
            view: CalendarView,
            tooltip: false,
            compact: true,
            props: {
                value: value,
                onChange: ( date ) => props.onChange( { name: props.name, value: date } ),
                closeOnSelection: true,
                selectRange: false,
                minDate
            },
            styles: {
                backgroundColor: 'transparent',
            }
        } );
    }, [ props.value ] );

    return (
        <div className={ widgetClassName }>

            { isEqual( props.mode, 'readonly' ) ? (
                <div className='uui-date-field__readonly'>
                    
                    {/* label */}
                    <div className='ui-date-field__readonly__label'>
                        { getLabel( props.label ) }
                    </div>

                    {/* input */}
                    <div className='ui-date-field__readonly__text'>
                        { props.value }
                    </div>
                </div>
            ) : (
                
                // {/* title */}
                <>
                    <div className='ui-date-field__label'>
                        <div className='ui-date-field__label__text'>{ getLabel( props.label ) }</div>
                        {
                            props.required ? (
                                <div className='ui-date-field__label__asterisk'>*</div>
                            ) : null
                        }
                    </div>

                    <div tabIndex='0' className={ inputClassName } onClick={ ( event ) => {

                        if( ! isOpen ) {
                            openClose( true );
                            openPopover( event.currentTarget );
                        } else {
                            openClose( false );
                            popover.close();
                        }

                    } }>

                        {
                            <div className='ui-date-field__input__value'>
                                <div className='ui-date-field__input__value__dates'>
                                    <div className='ui-date-field__input__value__dates__item'>{ isEmpty( value ) ? getLabel( props.placeholder ): value }</div>
                                </div>
                            </div>
                        }
                        
                        {/* {
                            isEmpty( value ) ? null : (
                                <div className='ui-date-field__input__value'>
                                    <div className='ui-date-field__input__value__dates'>
                                        <div className='ui-date-field__input__value__dates__item'>{ value }</div>
                                    </div>
                                </div>
                            )
                        } */}
                        
                        { ! readonly && (
                            <div className='ui-date-field__input__icon'>
                                <Icon name={ 'calendar' } />
                            </div>
                        ) }
                
                    </div>
                </>
            ) }
            {
                hasError ? (
                    <div className='ui-date-field__error'>
                        <div className='ui-date-field__error__message'>
                            { getLabel( errorMessage ) }
                        </div>
                    </div>
                ) : null
            }
        </div>
    );
};

// set display name
dateField.displayName = 'dateField';

// set default props
dateField.defaultProps = {
    showIcon: false
};

// prop types
dateField.propTypes = {};
